import { SearchX } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { manejarErrorDeUsuario } from "@/components/usuarios/errores";
import { UsuarioFormulario } from "@/components/usuarios/UsuarioFormulario";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { usePerfiles } from "@/hooks/usePerfiles";
import { useActualizarUsuario, useUsuarios } from "@/hooks/useUsuarios";
import { combinarEstados, estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { formatearRut } from "@/lib/rut";

/**
 * Ficha de usuario (RF-B3): los seis campos editables, más el teléfono
 * opcional.
 *
 * El API no expone `GET /api/users/{user}`, así que el usuario sale del
 * listado cacheado — mismo camino que la edición de perfil.
 */
export function UsuarioEditar() {
  const { id } = useParams();
  const navigate = useNavigate();
  const usuarios = useUsuarios();
  const perfiles = usePerfiles();
  const actualizar = useActualizarUsuario();
  const { user: autenticado } = useAuth();

  const usuario = usuarios.data?.find((u) => u.id === Number(id));
  const error = usuarios.error ?? perfiles.error;
  /* La ficha necesita DOS consultas (el usuario y el maestro de perfiles): con
     una sola pausada, "no existe" sería mentira. Ver `lib/estadoDeConsulta.ts`. */
  const estado = combinarEstados(estadoDeConsulta(usuarios), estadoDeConsulta(perfiles));

  /* D12 del backend: un Super Admin no puede cambiar su propio perfil, y el
     rechazo es un 422. La ficha propia muestra el perfil pero no lo deja
     tocar, para no mandar algo que ya se sabe rechazado. */
  const esFichaPropia = Boolean(autenticado && usuario && autenticado.id === usuario.id);

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to="/admin/usuarios">Volver al listado</EnlaceVolver>

      {estado.hayError ? (
        <EstadoDeError
          titulo="No pudimos cargar la ficha"
          error={error}
          onReintentar={() => {
            usuarios.refetch();
            perfiles.refetch();
          }}
          reintentando={estado.recargando}
        />
      ) : estado.sinConexion ? (
        <EstadoSinConexion que="la ficha del usuario" />
      ) : estado.cargando ? (
        <div
          role="status"
          aria-label="Cargando la ficha"
          className="bg-card w-full max-w-[40rem] space-y-5 rounded-xl border p-6"
        >
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-full rounded-md" />
          <Skeleton className="h-11 w-40 rounded-md" />
        </div>
      ) : !usuario ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchX />
            </EmptyMedia>
            <EmptyTitle>No encontramos ese usuario</EmptyTitle>
            <EmptyDescription>
              Puede que lo hayan editado desde otra sesión. Vuelve al listado y búscalo ahí.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild variant="outline">
              <Link to="/admin/usuarios">Volver al listado</Link>
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        <div className="space-y-5">
          {/* Identidad de a quién se le está tocando la ficha (prototipo). */}
          <div className="flex items-center gap-3">
            <InitialsAvatar nombre={usuario.name} className="size-10" />
            <div>
              <p className="text-sm font-bold">{usuario.name}</p>
              <p className="text-muted-foreground font-mono text-xs">
                {usuario.rut ? `${formatearRut(usuario.rut)} · ` : ""}
                {usuario.email}
              </p>
            </div>
          </div>

          {/* El `key` REMONTA el formulario al cambiar de usuario:
              react-hook-form solo lee `defaultValues` al montar, así que sin
              esto pasar de una ficha a otra dejaría los datos del anterior. */}
          <UsuarioFormulario
            key={usuario.id}
            valoresIniciales={{
              first_name: usuario.first_name,
              last_name: usuario.last_name,
              // Los campos son obligatorios en la aplicación pero nullable en
              // base: una ficha vieja precarga vacío y el usuario los completa.
              rut: usuario.rut ?? "",
              cargo: usuario.cargo ?? "",
              // Acá `null` no es una ficha vieja sino una decisión: el
              // teléfono es opcional. Precarga vacío igual.
              telefono: usuario.telefono ?? "",
              email: usuario.email,
              perfil_id: usuario.perfil ? String(usuario.perfil.id) : "",
            }}
            perfiles={perfiles.data ?? []}
            perfilBloqueado={
              esFichaPropia
                ? "No puedes cambiar tu propio perfil: dejarías a la plataforma sin quién la administre."
                : undefined
            }
            etiquetaEnvio="Guardar cambios"
            enviando={actualizar.isPending}
            onEnviar={(valores, { errorDeCampo }) =>
              actualizar.mutate(
                {
                  id: usuario.id,
                  first_name: valores.first_name,
                  last_name: valores.last_name,
                  email: valores.email,
                  rut: valores.rut,
                  cargo: valores.cargo,
                  // Va SIEMPRE, incluso vacío: el backend deja el número
                  // intacto cuando la clave no viaja, así que omitirlo haría
                  // imposible borrarlo desde esta pantalla.
                  telefono: valores.telefono.trim() || null,
                  perfil_id: Number(valores.perfil_id),
                },
                {
                  onSuccess: (actualizado) => {
                    toast.success(`Ficha de ${actualizado.name} actualizada`);
                    navigate("/admin/usuarios");
                  },
                  onError: (error) =>
                    manejarErrorDeUsuario(
                      error,
                      errorDeCampo,
                      "No pudimos guardar la ficha. Inténtalo de nuevo.",
                    ),
                },
              )
            }
            onCancelar={() => navigate("/admin/usuarios")}
          />
        </div>
      )}
    </div>
  );
}
